/**
 * The editor store: the scene history (undo/redo), the selection, the Data panel's test values, UI toggles and
 * the autosave state. One immutable state object, replaced on every change; React reads it with
 * useSyncExternalStore.
 *
 * Autosave: every change marks the draft dirty and schedules a PUT (debounced, with a maximum wait). Each PUT
 * names the revision it is based on; 409 REVISION_CONFLICT stops autosave and asks the user to reload or
 * overwrite.
 *
 * Read-only (the PRISM session expired): scene edits are refused and autosave pauses until it is lifted (a fresh
 * token); pending changes then save.
 *
 * Layouts (ADR 0037): the history holds the whole graphic (`document`); `scene` is what the editor shows, the active
 * layout (`ui.layout`) as an ordinary scene. Edits made there go back through `foldLayoutEdit` (model/layouts.ts) in
 * apply, preview and commitFrom, one undo step as before; in the primary layout `reconcileLayouts` keeps the other
 * layouts' overrides valid.
 */
import { useSyncExternalStore } from 'react';
import type { Scene } from '@prism-graphics/scene-schema';
import { ApiError, api } from '../api.ts';
import { modelGeo, type Geo } from '../model/commands.ts';
import { canRedo, canUndo, commit, commitFrom, createHistory, preview, redo, reset, undo, type History } from '../model/history.ts';
import { addLayout, applyInLayouts, foldLayoutEdit, hasLayouts, primaryOf, reconcileLayouts, removeLayout, replaceLayout, resetOverrides, resolveLayout, sourceOf, viewOf, type OverrideKey } from '../model/layouts.ts';
import { allIds } from '../model/tree.ts';
import { autoKey, getPhase, type KeyClip, type KeyRef } from '../model/timeline.ts';
import type { PreviewTarget } from '../model/preview.ts';

export type SaveStatus = 'saved' | 'dirty' | 'saving' | 'error' | 'conflict';

export interface UiState {
  showSafe: boolean;
  actionSafe: number;
  graphicsSafe: number;
  showCut: boolean;
  showRulers: boolean;
  showGuides: boolean;
  snapping: boolean;
  showReference: boolean;
  renderState: 'hold' | 'design';
  guides: { x: number[]; y: number[] };
  rightTab: 'design' | 'data' | 'brand';
  bottomTab: 'problems' | 'timeline';
  zoom: 'fit' | number;
  shortcutsOpen: boolean;
  /** "Change format…" is open with this target size preselected. */
  formatDialog: { width: number; height: number } | null;
  collapsed: string[];
  /** Canvas drags and nudges record keyframes at the playhead (timeline open). */
  autoKey: boolean;
  /** Timeline zoom: pixels per second. */
  timelineZoom: number;
  /** Time display on the timeline: frames, timecode or milliseconds. */
  timeFormat: 'frames' | 'timecode' | 'ms';
  /** The layout being edited (its id, "9x16"); null or an id the graphic lacks: the primary. Not saved. */
  layout: string | null;
  /** "Change format…" adds a layout instead of changing the active one. */
  formatDialogMode?: 'change' | 'add';
}

/** The timeline's transient state (not part of the scene). */
export interface AnimState {
  /** The phase being edited and previewed, or the whole graphic ("play through", read-only). */
  target: PreviewTarget;
  /** Playhead, ms of local time in the target. */
  time: number;
  playing: boolean;
  /** Selected keyframes in the target phase. */
  keys: KeyRef[];
}

export interface EditorState {
  draftId: string;
  revision: number;
  history: History<Scene>;
  selection: string[];
  testData: Record<string, unknown>;
  save: { status: SaveStatus; lastSavedAt?: string; error?: string; conflict?: { currentRevision: number; updatedAt: string } };
  ui: UiState;
  /** Bumped to ask the inspector to focus the text content of the selection. */
  focusText: number;
  anim: AnimState;
  /** The PRISM graphic this draft edits, the version it is based on, and changes since it was opened or saved. */
  prism: PrismLink;
  /** No scene edits and no autosave (the PRISM session expired). */
  readOnly: boolean;
}

export interface PrismLink {
  assetId: string | null;
  version: number | null;
  /** Scene changes not saved to PRISM yet. */
  dirty: boolean;
}

export const DEFAULT_UI: UiState = {
  showSafe: true,
  actionSafe: 3.5,
  graphicsSafe: 5,
  showCut: false,
  showRulers: true,
  showGuides: true,
  snapping: true,
  showReference: true,
  renderState: 'hold',
  guides: { x: [], y: [] },
  rightTab: 'design',
  bottomTab: 'problems',
  zoom: 'fit',
  shortcutsOpen: false,
  formatDialog: null,
  collapsed: [],
  autoKey: false,
  timelineZoom: 300,
  timeFormat: 'frames',
  layout: null,
};

/** The id of the layout being edited (the primary's when ui.layout names none the graphic has). */
export const activeLayout = (s: EditorState): string => resolveLayout(s.history.present, s.ui.layout);

/** The scene the editor shows: the active layout as an ordinary scene (the graphic itself in the primary layout). */
export const sceneView = (s: EditorState): Scene => viewOf(s.history.present, activeLayout(s));

export const DEFAULT_ANIM: AnimState = { target: { kind: 'in' }, time: 0, playing: false, keys: [] };

const DEBOUNCE_MS = 1200;
const MAX_WAIT_MS = 5000;
const RETRY_MS = 5000;

function readLocal<T>(key: string, fallback: T): T {
  try {
    const v = localStorage.getItem(key);
    return v ? ({ ...fallback, ...JSON.parse(v) } as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeLocal(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // storage unavailable: settings last for the session
  }
}

export class EditorStore {
  state: EditorState;
  /** Canvas geometry for commands that keep elements in place (set by the canvas after each render). */
  geo: Geo;
  #listeners = new Set<() => void>();
  #timer: ReturnType<typeof setTimeout> | undefined;
  #firstDirtyAt = 0;
  #saving: Promise<void> | undefined;
  #disposed = false;
  #discarded = false;
  /** Called when an autosave is refused with 401 (the embedded editor expires its PRISM session). */
  onUnauthorized: (() => void) | undefined;

  constructor(draft: { id: string; revision: number; scene: Scene; updatedAt?: string; prismAssetId?: string | null; prismBaseVersion?: number | null }) {
    const ui = readLocal<UiState>('pg.ui', DEFAULT_UI);
    const guides = readLocal<{ x: number[]; y: number[] }>(`pg.guides.${draft.id}`, { x: [], y: [] });
    const testData = readLocal<Record<string, unknown>>(`pg.test.${draft.id}`, {});
    this.state = {
      draftId: draft.id,
      revision: draft.revision,
      history: createHistory(draft.scene),
      selection: [],
      testData,
      save: { status: 'saved', ...(draft.updatedAt ? { lastSavedAt: draft.updatedAt } : {}) },
      ui: { ...DEFAULT_UI, ...ui, guides, shortcutsOpen: false, zoom: 'fit', layout: null },
      focusText: 0,
      anim: DEFAULT_ANIM,
      prism: { assetId: draft.prismAssetId ?? null, version: draft.prismBaseVersion ?? null, dirty: false },
      readOnly: false,
    };
    this.geo = modelGeo(draft.scene);
  }

  /** The scene the editor shows and commands edit: the active layout (see the header). */
  get scene(): Scene {
    return sceneView(this.state);
  }

  /** The whole graphic, all layouts (what is saved and exported). */
  get document(): Scene {
    return this.state.history.present;
  }

  /** The active layout's id. */
  get layout(): string {
    return activeLayout(this.state);
  }

  /** Whether an edit now changes the primary layout (or the graphic has one layout). */
  get inPrimary(): boolean {
    return this.layout === primaryOf(this.document);
  }

  /** Geometry of a scene in another layout (an off-screen render; set by the canvas). Falls back to modelGeo. */
  measure: ((scene: Scene) => Geo) | undefined;

  /** The base of the drag being previewed (the document when it started) and the last scene it showed. */
  #previewBase: Scene | undefined;
  #previewShown: Scene | undefined;

  /** `edited` (a change of the scene shown in `layout`) as a change of `doc`. */
  #fold(doc: Scene, layout: string, edited: Scene): Scene {
    if (!hasLayouts(doc)) return edited;
    if (layout === primaryOf(doc)) return reconcileLayouts(doc, edited);
    return foldLayoutEdit(doc, layout, edited);
  }

  subscribe = (fn: () => void): (() => void) => {
    this.#listeners.add(fn);
    return () => this.#listeners.delete(fn);
  };

  getState = (): EditorState => this.state;

  #set(patch: Partial<EditorState>): void {
    this.state = { ...this.state, ...patch };
    for (const l of this.#listeners) l();
  }

  // -------------------------------------------------------------------------------------------------------------
  // Scene edits

  /** Apply a command; `key` merges quick successive edits of one control into one undo step. */
  apply(fn: (scene: Scene) => Scene, opts: { key?: string; select?: string[] } = {}): void {
    if (this.state.readOnly) {
      if (opts.select) this.select(opts.select);
      return;
    }
    const prev = this.scene;
    const edited = fn(prev);
    if (edited === prev) {
      if (opts.select) this.select(opts.select);
      return;
    }
    this.#previewBase = undefined;
    const next = this.#fold(this.document, this.layout, edited);
    this.#commitDocument(next, opts);
  }

  /**
   * Apply a command in every layout, each with its own geometry (grouping, ungrouping, reparenting, duplicating: they
   * keep elements where they are on screen, which differs per layout). One undo step.
   */
  applyEach(fn: (scene: Scene, geo: Geo) => Scene, opts: { select?: string[] } = {}): void {
    if (this.state.readOnly || !hasLayouts(this.document)) return this.apply((s) => fn(s, this.geo), opts);
    const active = this.layout;
    const doc = this.document;
    const next = applyInLayouts(doc, (flat, id) => fn(flat, id === active ? this.geo : (this.measure?.(flat) ?? modelGeo(flat))));
    if (next === doc) {
      if (opts.select) this.select(opts.select);
      return;
    }
    this.#previewBase = undefined;
    this.#commitDocument(next, opts);
  }

  /** Commit a new document (all layouts). */
  #commitDocument(next: Scene, opts: { key?: string; select?: string[] }): void {
    if (this.state.readOnly) return;
    if (next === this.document) {
      if (opts.select) this.select(opts.select);
      return;
    }
    const history = commit(this.state.history, next, opts.key !== undefined ? { key: opts.key } : {});
    this.#set({ history, selection: this.#validSelection(next, opts.select ?? this.state.selection) });
    this.#dirty();
  }

  /** Keyframes copied in the timeline (in memory; also put on the system clipboard as JSON). */
  keyClip: KeyClip | undefined;

  /** Whether the timeline is open (the canvas then shows the frame at the playhead). */
  get timelineOpen(): boolean {
    return this.state.ui.bottomTab === 'timeline';
  }

  setAnim(patch: Partial<AnimState>): void {
    this.#set({ anim: { ...this.state.anim, ...patch } });
  }

  /** With auto-key on (and a phase open), turn the design change base → next into keyframes at the playhead. */
  autoKeyed(base: Scene, next: Scene): Scene {
    const { ui, anim } = this.state;
    if (!ui.autoKey || !this.timelineOpen || anim.target.kind === 'through' || !getPhase(base, anim.target)) return next;
    return autoKey(base, next, anim.target, anim.time);
  }

  /** Show a scene (a change of `scene`) during a drag without recording it. */
  preview(scene: Scene): void {
    if (this.state.readOnly) return;
    // A drag starts from the document shown when its first preview came (anything else since then starts anew).
    if (!this.#previewBase || this.document !== this.#previewShown) this.#previewBase = this.document;
    const next = sourceOf(scene) ?? this.#fold(this.#previewBase, this.layout, scene);
    this.#previewShown = next;
    this.#set({ history: preview(this.state.history, next) });
  }

  /** End a drag: one undo step from `base` (the scene shown when it started) to `scene`. */
  commitFrom(base: Scene, scene: Scene): void {
    const baseDoc = sourceOf(base) ?? (this.#previewBase && hasLayouts(this.#previewBase) && !hasLayouts(base) ? this.#previewBase : base);
    this.#previewBase = undefined;
    if (this.state.readOnly) {
      // Became read-only during a drag: drop what it showed.
      this.#set({ history: preview(this.state.history, baseDoc) });
      return;
    }
    const present = this.document;
    const next = scene === base ? baseDoc : scene === present || sourceOf(scene) === present ? present : (sourceOf(scene) ?? this.#fold(baseDoc, this.layout, scene));
    this.#set({ history: commitFrom(this.state.history, baseDoc, next) });
    if (next !== baseDoc) this.#dirty();
  }

  // -------------------------------------------------------------------------------------------------------------
  // Layouts

  /** Edit another layout (null: the primary). Not an edit: no undo step. */
  setLayout(id: string | null): void {
    const layout = id && id !== primaryOf(this.document) ? id : null;
    if (layout === this.state.ui.layout) return;
    this.#set({ ui: { ...this.state.ui, layout }, anim: { ...this.state.anim, playing: false } });
  }

  /** Add a layout made from `flat` (the primary adapted to its size) and edit it. One undo step. */
  addLayout(flat: Scene): void {
    if (this.state.readOnly) return;
    this.#previewBase = undefined;
    this.#commitDocument(addLayout(this.document, flat), {});
    this.setLayout(primaryOf(flat));
  }

  /** Re-adapt layout `id` from `flat` (its new size and overrides; a new aspect renames it). One undo step. */
  replaceLayout(id: string, flat: Scene): void {
    if (this.state.readOnly) return;
    this.#previewBase = undefined;
    this.#commitDocument(replaceLayout(this.document, id, flat), {});
    this.setLayout(primaryOf(flat));
  }

  /** "Reset to primary": an element follows the primary layout again in the active layout for `keys`. */
  resetOverrides(elementId: string, keys: readonly OverrideKey[]): void {
    if (this.state.readOnly || this.inPrimary) return;
    this.#previewBase = undefined;
    this.#commitDocument(resetOverrides(this.document, this.layout, elementId, keys), {});
  }

  /** Remove layout `id` (not the primary); the primary is edited next. One undo step. */
  removeLayout(id: string): void {
    if (this.state.readOnly || id === primaryOf(this.document)) return;
    this.#previewBase = undefined;
    if (this.layout === id) this.setLayout(null);
    this.#commitDocument(removeLayout(this.document, id), {});
  }

  undo(): void {
    this.#previewBase = undefined;
    if (this.state.readOnly || !canUndo(this.state.history)) return;
    const history = undo(this.state.history);
    this.#set({ history, selection: this.#validSelection(history.present, this.state.selection) });
    this.#dirty();
  }

  redo(): void {
    this.#previewBase = undefined;
    if (this.state.readOnly || !canRedo(this.state.history)) return;
    const history = redo(this.state.history);
    this.#set({ history, selection: this.#validSelection(history.present, this.state.selection) });
    this.#dirty();
  }

  #validSelection(scene: Scene, ids: readonly string[]): string[] {
    const all = allIds(scene);
    return ids.filter((id) => all.has(id));
  }

  select(ids: readonly string[]): void {
    const next = this.#validSelection(this.scene, ids);
    if (next.length === this.state.selection.length && next.every((id, i) => id === this.state.selection[i])) return;
    this.#set({ selection: next });
  }

  requestTextFocus(): void {
    this.#set({ focusText: this.state.focusText + 1, ui: { ...this.state.ui, rightTab: 'design' } });
  }

  // -------------------------------------------------------------------------------------------------------------
  // Test data and UI

  setTestValue(fieldId: string, value: unknown): void {
    const testData = { ...this.state.testData, [fieldId]: value };
    if (value === undefined) delete testData[fieldId];
    this.#set({ testData });
    writeLocal(`pg.test.${this.state.draftId}`, testData);
  }

  resetTestData(): void {
    this.#set({ testData: {} });
    writeLocal(`pg.test.${this.state.draftId}`, {});
  }

  setUi(patch: Partial<UiState>): void {
    const ui = { ...this.state.ui, ...patch };
    this.#set({ ui });
    const { guides, shortcutsOpen: _s, formatDialog: _f, formatDialogMode: _m, zoom: _z, layout: _l, ...persist } = ui;
    if (patch.bottomTab && patch.bottomTab !== 'timeline' && this.state.anim.playing) this.setAnim({ playing: false });
    writeLocal('pg.ui', persist);
    if (patch.guides) writeLocal(`pg.guides.${this.state.draftId}`, guides);
  }

  // -------------------------------------------------------------------------------------------------------------
  // Autosave

  #dirty(): void {
    if (!this.state.prism.dirty) this.#set({ prism: { ...this.state.prism, dirty: true } });
    if (this.state.save.status === 'conflict') return;
    if (this.state.save.status !== 'saving') this.#set({ save: { ...this.state.save, status: 'dirty' } });
    if (!this.#firstDirtyAt) this.#firstDirtyAt = Date.now();
    this.#schedule(Math.min(DEBOUNCE_MS, Math.max(0, MAX_WAIT_MS - (Date.now() - this.#firstDirtyAt))));
  }

  #schedule(ms: number): void {
    if (this.#timer) clearTimeout(this.#timer);
    this.#timer = undefined;
    if (this.#disposed || this.state.readOnly) return;
    this.#timer = setTimeout(() => void this.saveNow(), ms);
  }

  get dirty(): boolean {
    return this.state.save.status === 'dirty' || this.state.save.status === 'error' || this.state.save.status === 'saving';
  }

  /**
   * Save now (Ctrl+S, page hide, closing the editor); waits for a save in flight first. A call made before
   * dispose() still saves once (the final flush when the editor closes); after dispose only keepalive saves run.
   */
  async saveNow(opts: { keepalive?: boolean } = {}): Promise<void> {
    if (this.#timer) clearTimeout(this.#timer);
    this.#timer = undefined;
    const allowed = !this.#disposed || opts.keepalive === true;
    // A loop: another caller waiting on the same save may have started the next one.
    while (this.#saving) await this.#saving;
    if (!allowed || this.#discarded || this.state.readOnly) return;
    const s = this.state.save.status;
    if (s === 'saved' || s === 'conflict') return;
    this.#saving = this.#save(opts).finally(() => {
      this.#saving = undefined;
    });
    return this.#saving;
  }

  async #save(opts: { keepalive?: boolean }): Promise<void> {
    const scene = this.document;
    this.#firstDirtyAt = 0;
    this.#set({ save: { ...this.state.save, status: 'saving' } });
    try {
      const r = await api.saveDraft(this.state.draftId, this.state.revision, scene, opts);
      const changed = this.document !== scene;
      this.#set({ revision: Math.max(this.state.revision, r.revision), save: { status: changed ? 'dirty' : 'saved', lastSavedAt: r.updatedAt } });
      if (changed) this.#dirty();
    } catch (e) {
      if (e instanceof ApiError && e.code === 'REVISION_CONFLICT') {
        const d = e.details as { currentRevision: number; updatedAt: string } | undefined;
        this.#set({ save: { ...this.state.save, status: 'conflict', conflict: { currentRevision: d?.currentRevision ?? this.state.revision, updatedAt: d?.updatedAt ?? '' } } });
        return;
      }
      this.#set({ save: { ...this.state.save, status: 'error', error: e instanceof Error ? e.message : String(e) } });
      if (e instanceof ApiError && e.status === 401) this.onUnauthorized?.();
      this.#schedule(RETRY_MS);
    }
  }

  /** Conflict: keep my version (save against the current revision). */
  async overwrite(): Promise<void> {
    const c = this.state.save.conflict;
    if (!c) return;
    this.#set({ revision: c.currentRevision, save: { status: 'dirty' } });
    await this.saveNow();
  }

  /** Conflict (or explicit reload): take the server's version; history starts again. */
  async reload(): Promise<void> {
    const d = await api.getDraft(this.state.draftId);
    this.#set({ revision: d.revision, history: reset(d.scene), selection: [], save: { status: 'saved', lastSavedAt: d.updatedAt } });
  }

  /**
   * After a save to PRISM: the draft revision the API moved to, the graphic and version, and the scene's name,
   * description and id as saved (no undo step for them). Changes made during the save keep the draft dirty.
   *
   * `draftChanged`: the draft changed on the server during the save (another tab, or this tab's own autosave); the
   * API kept that newer scene, unsaved in PRISM. If this tab's scene has not changed since the save started, the
   * newer scene came from elsewhere and this tab's copy is stale: it keeps its old revision (so an edit autosaved
   * meanwhile meets REVISION_CONFLICT instead of overwriting) and loads the draft from the server.
   */
  async prismSaved(r: { assetId: string; version: number; revision: number; updatedAt: string; name: string; sceneId: string; draftChanged?: boolean }, savedScene: Scene, description?: string): Promise<void> {
    const changedSince = this.document !== savedScene;
    const stale = r.draftChanged === true && !changedSince;
    // The id, name and description are the PRISM graphic's identity, not edits: every history entry takes them, so
    // undo or redo after "Save as copy" never brings back the original graphic's id.
    const withMeta = (scene: Scene): Scene => {
      const meta: Scene['meta'] = { ...scene.meta, name: r.name, id: r.sceneId };
      if (description !== undefined) {
        if (description) meta.description = description;
        else delete meta.description;
      }
      return { ...scene, meta };
    };
    const h = this.state.history;
    const history = { ...h, past: h.past.map(withMeta), present: withMeta(h.present), future: h.future.map(withMeta) };
    this.#set({
      history,
      // The API may report a newer revision than this save's own (autosaves that landed meanwhile); never go back.
      revision: stale ? this.state.revision : Math.max(this.state.revision, r.revision),
      prism: { assetId: r.assetId, version: r.version, dirty: changedSince || stale },
      save: changedSince || stale ? this.state.save : { status: 'saved', lastSavedAt: r.updatedAt },
    });
    // The API already stored the saved scene at this revision; a newer local scene autosaves on top of it.
    if (changedSince) this.#dirty();
    if (stale) await this.#reloadUnlessEdited();
  }

  /**
   * Take the server's draft unless this tab's scene changes while it loads (that edit then meets REVISION_CONFLICT
   * and the user chooses). A failed load leaves the old revision, so the next autosave conflicts the same way.
   */
  async #reloadUnlessEdited(): Promise<void> {
    const at = this.document;
    try {
      const d = await api.getDraft(this.state.draftId);
      if (this.#disposed || this.document !== at) return;
      this.#set({ revision: d.revision, history: reset(d.scene), selection: [], save: { status: 'saved', lastSavedAt: d.updatedAt } });
    } catch {
      // The old revision stays: the next autosave conflicts and the user chooses.
    }
  }

  /** Read-only on (session expired): edits refused, autosave paused. Off again: pending changes save. */
  setReadOnly(on: boolean): void {
    if (on === this.state.readOnly) return;
    this.#set({ readOnly: on });
    if (on) {
      if (this.#timer) clearTimeout(this.#timer);
      this.#timer = undefined;
    } else if (this.state.save.status === 'dirty' || this.state.save.status === 'error') {
      this.#schedule(0);
    }
  }

  /** Stop autosaving (timers and retries). A saveNow() called before this still runs once. */
  dispose(): void {
    this.#disposed = true;
    if (this.#timer) clearTimeout(this.#timer);
    this.#timer = undefined;
  }

  /**
   * Discard the unsaved changes for good (closing from PRISM with Discard): no more autosaves, and the draft is
   * deleted so the next open starts from PRISM's version.
   */
  async discard(): Promise<void> {
    this.#discarded = true;
    this.dispose();
    if (this.#saving) await this.#saving.catch(() => undefined);
    this.#set({ save: { ...this.state.save, status: 'saved' } });
    await api.deleteDraft(this.state.draftId).catch(() => undefined);
  }
}

export function useEditor<T>(store: EditorStore, select: (s: EditorState) => T): T {
  return useSyncExternalStore(store.subscribe, () => select(store.state));
}
